import api from '../client';

export interface Employer {
  uuid: string;
  name: string;
  abn: string;
  contact_name: string | null;
  contact_email: string;
  contact_phone: string | null;
  head_office_location: string | null;
  employee_count?: number;
  status?: 'approved' | 'unapproved' | 'rejected' | 'seeded';
  /** Actual DB status (use for admin UI when status is mapped for mobile compat). */
  db_status?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface EmployerFilters {
  phrase?: string;
  status?: string;
  page?: number;
  per_page?: number;
  column?: string;
  order?: 'asc' | 'desc';
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface EmployerListSummary {
  total: number;
  seeded: number;
  approved: number;
  unapproved: number;
  rejected: number;
}

export interface EmployerResponse {
  success: boolean;
  message: string;
  data: Employer[];
  paging: PaginationInfo;
  summary?: EmployerListSummary;
}

class EmployersService {
  private baseUrl = '/employer';

  /**
   * Get list of employers with optional filters
   */
  async getEmployers(filters?: EmployerFilters): Promise<EmployerResponse> {
    try {
      const response = await api.get<EmployerResponse>(this.baseUrl, { params: filters });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Unauthorized access');
      }
      if (error.response?.status === 403) {
        throw new Error('Permission denied');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch employers');
    }
  }

  /**
   * Get a single employer by UUID
   */
  async getEmployerByUuid(uuid: string): Promise<EmployerResponse> {
    try {
      const response = await api.get<EmployerResponse>(`${this.baseUrl}/${uuid}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch employer details');
    }
  }

  /**
   * Create a new employer
   */
  async createEmployer(data: Partial<Employer>): Promise<EmployerResponse | any> {
    try {
      const response = await api.post<EmployerResponse>(this.baseUrl, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        return {
          success: false,
          message: error.response.data.message || 'Validation failed',
          data: error.response.data.data || error.response.data.errors || {},
          error: error
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create employer',
        error: error
      };
    }
  }

  /**
   * Update an existing employer
   */
  async updateEmployer(uuid: string, data: Partial<Employer>): Promise<EmployerResponse | any> {
    try {
      const response = await api.put<EmployerResponse>(`${this.baseUrl}/${uuid}`, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        return {
          success: false,
          message: error.response.data.message || 'Validation failed',
          data: error.response.data.data || error.response.data.errors || {},
          error: error
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update employer',
        error: error
      };
    }
  }

  /**
   * Format date for display (e.g. Created on column)
   */
  formatDate(date: string | null | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-AU');
  }

  /**
   * Delete an employer
   */
  async deleteEmployer(uuid: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`${this.baseUrl}/${uuid}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete employer');
    }
  }
}

export const employersService = new EmployersService();
export default employersService;

